import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { useLead } from '../contexts/LeadContext';
import { MODELS } from '../data/models';
import { PhotoPlaceholder } from './PhotoPlaceholder';
import { Reveal } from './Reveal';
import { trackVehicleInterest } from '../tracking';
import { MODELS_ID, scrollToLeadForm } from '../utils/scroll';

/**
 * Model interest capture. Picking a card stores the model in shared state, fires
 * VehicleInterest, and pre-fills the form's vehicle field.
 *
 * Full-bleed photography with the type set over it — the photograph is the card, not
 * a thumbnail inside one. No tilt, no shadow, no border: the image is the decoration.
 *
 * The only treatment over a photo is a bottom-anchored scrim, present solely so white
 * type stays legible. No color overlay, duotone, or filter touches the image itself.
 */
export function MostWanted() {
  const {
    t
  } = useLanguage();
  const {
    vehicleInterest,
    setVehicleInterest
  } = useLead();
  const reduce = useReducedMotion();
  const choose = (id: (typeof MODELS)[number]['id'], name: string) => {
    setVehicleInterest(id);
    trackVehicleInterest(name);
    scrollToLeadForm();
  };
  return <section id={MODELS_ID} className="w-full scroll-mt-24 bg-white" data-id="element-341" data-mp="12xi4q3f8zyxs">
      <div className="mx-auto w-full max-w-7xl px-4 py-16 sm:px-6 md:py-20" data-id="element-342" data-mp="17wbizy2cztz0">
        <Reveal data-id="element-343" data-mp="t8grgl037vni">
          <h2 className="font-display text-3xl font-extrabold uppercase leading-[1.02] tracking-[-0.03em] text-ink sm:text-[2.75rem]" data-id="element-344" data-mp="t7yl2vjpo11v">
            {t('wanted.title')}
          </h2>
          <div aria-hidden="true" className="mt-3 h-[3px] w-12 bg-kred" data-id="element-345" data-mp="k7sabhfedf6e" />
          <p className="mt-4 max-w-[52ch] text-base leading-relaxed text-body" data-id="element-346" data-mp="k5pf8ize0vut">{t('wanted.sub')}</p>
        </Reveal>

        <Reveal delay={0.1} className="mt-10" data-id="element-347" data-mp="a9mdvfcd118x">
          <ul className="-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-4 md:mx-0 md:grid md:grid-cols-3 md:gap-4 md:overflow-visible md:px-0 md:pb-0" data-id="element-348" data-mp="a9w516955eg7">
            {MODELS.map(model => {
            const selected = vehicleInterest === model.id;
            return <li key={model.id} className="w-[84vw] shrink-0 snap-center sm:w-[62vw] md:w-auto" data-id="element-349" data-mp="3nhq64nwn9318">
                  <motion.button type="button" onClick={() => choose(model.id, model.name)} aria-pressed={selected} whileHover={reduce ? undefined : {
                y: -4
              }} transition={{
                type: 'spring',
                stiffness: 300,
                damping: 26
              }} className="group relative block aspect-[3/4] w-full overflow-hidden bg-ink text-left" data-id="element-350" data-mp="3ik6t1rbomwav">
                    {model.photo ? <img src={model.photo} alt={model.name} loading="lazy" decoding="async" className={`h-full w-full object-cover transition-transform duration-700 ease-out ${reduce ? '' : 'group-hover:scale-[1.04]'}`} data-id="element-351" data-mp="4e3leh0rsgf" /> : <PhotoPlaceholder label={t('wanted.photo', {
                  model: model.name.toUpperCase()
                })} tone="dark" data-id="element-352" data-mp="15ackfx2zyvyq" />}

                    {/* Legibility scrim only — bottom-anchored, no tint across the image. */}
                    <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-ink via-ink/70 to-transparent" data-id="element-353" data-mp="1k3xuqr41l5sm" />

                    {/* Selected state reads as a red rule along the base, never a fill. */}
                    {selected && <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-1 bg-kred" data-id="element-354" data-mp="3uufyyopebb1w" />}

                    <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6" data-id="element-355" data-mp="3o4u0hh9lq5a">
                      <p className="font-display text-[10px] font-bold uppercase tracking-[0.22em] text-gold" data-id="element-356" data-mp="1u3nszd9fcvz5">
                        {t(model.line)}
                      </p>
                      <h3 className="mt-2 font-display text-2xl font-extrabold uppercase leading-none tracking-[-0.02em] text-white sm:text-3xl" data-id="element-357" data-mp="234vdmsj0pyar">
                        {model.name}
                      </h3>
                      <span className={`mt-4 inline-block text-[11px] font-bold uppercase tracking-[0.16em] ${selected ? 'text-kred' : 'text-white/75'}`} data-id="element-358" data-mp="1bdfvpbseozqv">
                        {t('wanted.cta')}
                      </span>
                    </div>
                  </motion.button>
                </li>;
          })}
          </ul>
          <p className="mt-3 text-xs text-muted md:hidden" data-id="element-359" data-mp="ro5gdywkogta">{t('wanted.swipe')}</p>
        </Reveal>
      </div>
    </section>;
}